import React, { useState, useRef, useEffect } from "react";

/**
 * A lightweight, dependency-free searchable single-select dropdown.
 * Styled with Tailwind to match the Game Goblin brand.
 */
export default function SearchableSelect({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Search or select...",
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const containerRef = useRef(null);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setInputValue(value || "");
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [value]);

  const filtered = options.filter((opt) =>
    opt.toLowerCase().includes((inputValue || "").toLowerCase())
  );

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <p className="text-brand-purple text-[13px] font-bold mb-1">{label}</p>
      )}
      <input
        type="text"
        value={inputValue}
        placeholder={loading ? "Loading..." : placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setInputValue(e.target.value);
          setOpen(true);
        }}
        className="w-full h-9 rounded-lg px-3 text-sm text-white bg-white/5 border border-brand-gold/30 outline-none transition-colors duration-150 focus:border-brand-gold placeholder:text-white/40"
      />
      {value && (
        <button
          type="button"
          onClick={() => {
            onChange("");
            setInputValue("");
          }}
          className="absolute right-2 top-[30px] text-white/40 hover:text-brand-gold text-sm"
          aria-label="Clear"
        >
          ✕
        </button>
      )}
      {open && !loading && (
        <div className="absolute z-30 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-brand-gold bg-surface-light/98 backdrop-blur-md shadow-card animate-fadeIn">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-white/50">No matches</div>
          ) : (
            filtered.map((opt) => (
              <div
                key={opt}
                onMouseDown={() => {
                  onChange(opt);
                  setInputValue(opt);
                  setOpen(false);
                }}
                className={[
                  "px-3 py-2 text-sm cursor-pointer  border-b  border-brand-gold/10 last:border-b-0 transition-colors",
                  opt === value
                    ? "bg-brand-purple/40 text-brand-gold font-semibold"
                    : "text-white hover:bg-brand-purple/20",
                ].join(" ")}
              >
                {opt}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
